import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { SkuRule } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useDeleteSkuRule } from '../hooks/use-sku-rule-mutations';

export interface SkuRuleDangerZoneProps {
  rule: SkuRule;
  /** Called once the backend has deleted the rule, to leave its page. */
  onDeleted: () => void;
}

/** Deleting never touches products: their style numbers and SKUs stay, their `skuRule` becomes null. */
export function SkuRuleDangerZone({ rule, onDeleted }: SkuRuleDangerZoneProps) {
  const { t } = useTranslation(['skuRules', 'common', 'errors']);
  const remove = useDeleteSkuRule();
  const message = useErrorMessage();
  const error = isApiError(remove.error) ? remove.error : null;

  return (
    <FormSection title={t('skuRules:danger.title')} className="border-destructive/30">
      <p className="text-muted-foreground text-sm">{t('skuRules:danger.description')}</p>
      {remove.isError ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {message(remove.error)}
            {error?.requestId ? (
              <span className="block text-xs">
                {t('errors:requestIdShort', { requestId: error.requestId })}
              </span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="text-destructive hover:text-destructive w-full"
            disabled={remove.isPending}
          >
            <Trash2 aria-hidden />
            {t('skuRules:danger.delete')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('skuRules:danger.confirmTitle', { name: rule.name })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('skuRules:danger.confirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove.mutate(rule.id, { onSuccess: onDeleted })}>
              {t('common:actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormSection>
  );
}
